import { useEffect, useRef, useState } from "react"
import { toast } from "sonner"

import { ReportIcon } from "@/components/icons"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command"
import {
  SherpaDialog,
  SherpaDialogContent,
  SherpaDialogTrigger,
  SherpaDialogHeader,
  SherpaDialogTitle,
} from "@/components/ui-kit/dialog"
import { useIsMobile } from "@/hooks/useIsMobile"
import { DocumentsApi, openDocument } from "@/lib/documents-api"
import { formatAmount, formatDateShort } from "@/lib/utils"
import { isPayable } from "@/lib/invoice-utils"
import type { Invoice } from "@/types/invoice"
import { Button as UiKitButton } from "@/components/ui-kit/button"
import { CreditCardIcon } from "@/components/icons"

function InvoiceActionsMenu({
  invoice,
  onClose,
}: {
  invoice: Invoice
  onClose: () => void
}) {
  const [isLoading, setIsLoading] = useState(false)

  const handleDownload = async () => {
    if (isLoading) {
      return
    }

    setIsLoading(true)

    try {
      const document = await DocumentsApi.getDocument("invoices", invoice.id)
      openDocument(document)
      onClose()
    } catch (error) {
      const message = error instanceof Error ? error.message : "Не удалось получить документ"
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Command className="p-0 rounded-[8px]! border-none! shadow-none!">
      <CommandList className="p-0">
        <CommandGroup className="p-0 divide-y divide-border">
          <CommandItem
            className="cursor-pointer sm:p-4 p-6 px-0 rounded-[0px] first:rounded-t-8 last:rounded-b-8 data-[selected=true]:bg-card hover:text-brand justify-between sm:justify-start"
            disabled={isLoading}
            onSelect={() => {
              void handleDownload()
            }}
          >
            <ReportIcon size={20} className="size-5 sm:order-1 order-2" />
            <span className="sm:order-2 order-1 text-[16px] leading-[22px] sm:text-[18px] sm:leading-[26px]">Скачать файл счета</span>
            <CommandShortcut className="hidden!" />
          </CommandItem>
          {/* <CommandSeparator />
          <CommandItem
            className="cursor-pointer p-4 rounded-[0px] first:rounded-t-8 last:rounded-b-8 data-[selected=true]:bg-card hover:text-brand"
            onSelect={onClose}
          >
            <ActIcon size={20} className="size-5" />
            <span>Скачать акт</span>
            <CommandShortcut className="hidden!" />
          </CommandItem> */}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

// What a customer is told about an invoice: a move's `settled` is paid for them, its `superseded` replaced.
function invoiceStatusLabel(invoice: Invoice): string {
  if (invoice.direction === "refund") return "Возврат"
  switch (invoice.status) {
    case "paid":
    case "settled":
      return "Оплачен"
    case "pending":
    case "pending_manual":
      return "К оплате"
    case "canceled":
      return "Отменён"
    case "superseded":
      return "Заменён"
    default:
      return invoice.status
  }
}

export function InvoiceLine({
  invoice,
  unitCode,
  showStatus = false,
  onPay,
}: {
  invoice: Invoice
  unitCode?: string
  showStatus?: boolean
  onPay?: () => void
}) {
  const [open, setOpen] = useState(false)
  const isMobile = useIsMobile()
  const prevIsMobile = useRef(isMobile)

  useEffect(() => {
    if (prevIsMobile.current !== isMobile) {
      setOpen(false)
      prevIsMobile.current = isMobile
    }
  }, [isMobile])

  const trigger = (
    <button type="button" className="mb-2 w-full cursor-pointer text-left text-lg leading-6 font-normal text-brand hover:text-[#70990c] inline-block">
      {invoice.name}
    </button>
  )

  return (
    <div role="group" aria-label={invoice.name} className="border-b border-border sm:py-6 py-4 first:pt-0 last:border-b-0 last:pb-0">
      {isMobile ? (
        <SherpaDialog open={open} onOpenChange={setOpen}>
          <SherpaDialogTrigger asChild>{trigger}</SherpaDialogTrigger>
          <SherpaDialogContent className="max-sm:left-0 max-sm:h-auto max-sm:max-h-[90dvh] max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:[--sherpa-dialog-height:auto]">
            <SherpaDialogHeader className="mb-2">
              <SherpaDialogTitle>Счет {invoice.name}</SherpaDialogTitle>
            </SherpaDialogHeader>
            <InvoiceActionsMenu invoice={invoice} onClose={() => setOpen(false)} />
          </SherpaDialogContent>
        </SherpaDialog>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent className="w-max p-0 rounded-[8px] border-none! ring-0! shadow-none! shadow-(--menu)! mt-2" align="start">
            <InvoiceActionsMenu invoice={invoice} onClose={() => setOpen(false)} />
          </PopoverContent>
        </Popover>
      )}

      <div className="flex flex-wrap items-center justify-between gap-4">
        {unitCode ? (
          <span className="text-lg leading-6 text-foreground">{unitCode}</span>
        ) : null}
        <span className="text-lg leading-6 text-foreground">
          {formatAmount(invoice.total ?? invoice.amount)}
        </span>
        {showStatus ? (
          <span className="text-lg leading-6 text-muted-foreground">{invoiceStatusLabel(invoice)}</span>
        ) : null}
        <span className="text-lg leading-6 text-muted-foreground">
          от {formatDateShort(invoice.createdAt)}
        </span>
      </div>

      {isPayable(invoice) && onPay && (
        <UiKitButton
          type="button"
          variant="primary"
          className="w-full sm:h-[60px] h-[48px] sm:text-lg text-[16px] leading-6 mt-3 sm:mt-4"
          endIcon={<CreditCardIcon size={24} className="size-[24px]" />}
          onClick={onPay}
        >
          Оплатить
        </UiKitButton>
      )}
    </div>
  )
}
